import {ChangeDetectionStrategy, Component, ViewEncapsulation} from '@angular/core';

/**
 * Reusable default avatar SVG component with theme-aware colors.
 * Automatically adapts to light/dark mode using CSS custom properties.
 *
 * @example
 * ```html
 * <default-avatar class="w-10 h-10 rounded-full"></default-avatar>
 * ```
 */
@Component({
  selector: 'default-avatar',
  standalone: true,
  template: `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1503 1503">
      <path
        fill="var(--avatar-bg)"
        d="M729 0h3l82 2a785 785 0 0 1 347 118l3 2a569 569 0 0 1 85 66l7 5 33 31 1 2 17 17 7 8 18 22 26 33 2 2a730 730 0 0 1 104 201l1 4q29 85 36 174v2q3 21 2 40v3l-2 82a785 785 0 0 1-118 347l-2 3-6 9-1 2a753 753 0 0 1-95 114l-2 1-17 17-8 7-22 18-33 26-2 2a730 730 0 0 1-201 104l-4 1q-85 28-174 36h-2q-21 3-40 2h-3l-82-2a785 785 0 0 1-347-118l-3-2a569 569 0 0 1-85-66l-7-5-32-31-2-2-17-17-7-8-18-22-26-33-2-2-21-31-1-2a844 844 0 0 1-82-168l-1-4Q10 905 2 816v-2l-2-40v-3l2-82a785 785 0 0 1 118-347l2-3 6-9 1-2a753 753 0 0 1 95-113l2-2 17-17 8-7 22-18a670 670 0 0 1 66-49l2-1a844 844 0 0 1 168-82l4-1q85-28 174-36h2z" />
      <path
        fill="var(--avatar-skin-1)"
        d="M755 229h3q53-1 102 15l3 1c52 17 102 48 135 93l4 6a335 335 0 0 1 56 138q5 22 6 44l1 3 2 51v3q0 28-3 56v3l-9 62q-7 47-22 92l-1 3a487 487 0 0 1-78 147l-2 2-2 7-1 3v3l-1 4-2 13c-4 30-5 65 2 94l1 3q10 46 50 72c37 22 80 34 122 45l58 17 2 1q46 13 86 40l3 2q12 10 22 21-3 6-7 9l-2 2-23 23-8 7-22 18-33 26-2 2a730 730 0 0 1-201 104l-4 1q-85 28-174 36h-2q-21 3-40 2h-3l-82-2a785 785 0 0 1-347-118l-3-2a569 569 0 0 1-85-66l-7-5-26-25-2-1q-4-3-3-6 4-7 10-12l2-2q17-17 38-28l2-1c36-21 80-32 121-42 91-22 91-22 159-81q15-28 18-60v-2l1-26-2-47c-2-16-4-28-15-40l-9-11-1-2a425 425 0 0 1-39-68 494 494 0 0 1-39-117q-12-56-17-115v-4l-1-40q0-31 3-62l1-3a334 334 0 0 1 109-222l2-2q18-13 37-24l2-1q24-13 51-22l2-1q35-10 70-13h3z" />
      <path
        fill="var(--avatar-skin-2)"
        d="M949 951v10l-1 5-1 7c-5 32-7 68 1 99l1 3q10 46 50 72c37 22 80 34 122 45l58 17 2 1q46 13 86 40l3 2q12 10 22 21-3 6-7 9l-2 2-23 23-8 7-22 18-33 26-2 2a730 730 0 0 1-201 104l-4 1q-85 28-174 36h-2q-21 3-40 2h-3l-82-2a785 785 0 0 1-347-118l-3-2-9-6-2-1a753 753 0 0 1-107-89l-2-1q-4-3-3-6 4-7 10-12l2-2q17-17 38-28l2-1c36-21 80-32 121-42 91-22 91-22 159-81q15-28 18-60v-2l1-26a440 440 0 0 0-5-71l9 9 20 21 16 15 2 2a262 262 0 0 0 113 57c52 10 106-7 150-35l32-25 3-2 23-23 8-8z" />
      <path
        fill="var(--avatar-skin-3)"
        d="M949 951v10l-1 5-1 7c-5 32-7 68 1 99l1 3q5 26 21 47l-10 7-4 1-3 2-3 1q-39 18-81 22h-2q-35 3-68-3l-4-1a292 292 0 0 1-118-50 393 393 0 0 1-83-74l-10-11v-2h-2l-3-3-1-3-2-2-3-4-2-2-3-3q-3-4-2-8l-1-3v-4l-1-3-2-26 9 9 20 21 16 15 2 2a262 262 0 0 0 113 57c52 10 106-7 150-35l32-25 3-2 23-23 8-8z" />
    </svg>
  `,
  styles: `
    default-avatar {
      --avatar-bg: #f5eee5;
      --avatar-skin-1: #f2cea5;
      --avatar-skin-2: #e6c19c;
      --avatar-skin-3: #d5b18d;

      display: block;
      width: 100%;
      height: 100%;
    }

    .dark default-avatar {
      --avatar-bg: #1e2939;
      --avatar-skin-1: #828282;
      --avatar-skin-2: #595759;
      --avatar-skin-3: #494949;
    }

    default-avatar svg {
      width: 100%;
      height: 100%;
      display: block;
    }
  `,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DefaultAvatarComponent {}
